<template>
  <div class="bg-gray-800 border border-gray-600
              print:bg-white print:border-0">
    <div v-if="isNew"
         class="flex items-center ml-4 mt-4">
      <div class="px-3 py-1 border border-blue-100 bg-blue-800 shadow-glowblue100
                  transform skew-x-20">
        <p class="uppercase text-xs font-semibold text-white tracking-widest
                  transform -skew-x-20">
          New
        </p>
      </div>
      <DashedLine class="hidden sm:block text-gray-300" />
      <div class="hidden sm:block px-3 py-1 border border-gray-600 bg-gray-800
                  transform skew-x-20">
        <p class="uppercase text-xs font-semibold text-gray-200 tracking-widest
                  transform -skew-x-20">
          Not detected in previous orbit
        </p>
      </div>
    </div>
    <div v-if="isResolved"
         class="flex items-center ml-4 mt-4">
      <div class="px-3 py-1 border border-green-500 bg-green-700 shadow-glowgreen
                  transform skew-x-20">
        <p class="uppercase text-xs font-semibold text-white tracking-widest
                  transform -skew-x-20">
          Resolved
        </p>
      </div>
      <DashedLine class="hidden sm:block text-gray-300" />
      <div class="hidden sm:block px-3 py-1 border border-gray-600 bg-gray-800
                  transform skew-x-20">
        <p class="uppercase text-xs font-semibold text-gray-200 tracking-widest
                  transform -skew-x-20">
          Not detected in this orbit
        </p>
      </div>
    </div>
    <!-- Desktop View -->
    <div v-if="rules.length <= 4"
         class="hidden flex-col p-4 print:flex print:py-2 lg:flex">
      <div class="w-full flex">
        <template v-for="(_, index) in rules">
          <h3 class="uppercase text-xs font-semibold text-gray-200 tracking-widest ml-2 mb-2
                     print:text-gray-300"
              :class="[
                {'w-1/5': rules.length === 4},
                {'w-1/4': rules.length === 3},
                {'w-1/3': rules.length === 2},
                {'w-1/2': rules.length === 1},
              ]"
              :key="`desktop-finding-h3-${index}`">
            Finding {{ index + 1 }}
          </h3>
          <div class="px-4"
               :key="`desktop-finding-spacer-${index}`"></div>
        </template>
        <h3 class="uppercase text-xs font-semibold tracking-widest ml-2 mb-2 print:text-orange-400"
            :class="[
              {'text-gray-200': isResolved},
              {'text-yellow-500': !isResolved},
              {'w-1/5': rules.length === 4},
              {'w-1/4': rules.length === 3},
              {'w-1/3': rules.length === 2},
              {'w-1/2': rules.length === 1},
            ]">
          Outcome
        </h3>
      </div>
      <div class="w-full flex">
        <template v-for="(_, index) in rules">
          <div class="flex justify-center items-center p-4 border border-gray-600
                      print:border-b-0 print:border-l-0 print:border-r-0 print:border-gray-100
                      print:p-2 print:text-gray-700 print:justify-start"
               :class="[
                 {'w-1/5': rules.length === 4},
                 {'w-1/4': rules.length === 3},
                 {'w-1/3': rules.length === 2},
                 {'w-1/2': rules.length === 1},
                ]"
               :key="`desktop-rule-${index}`">
            <p class="text-center print:text-left">
              {{ ruleLabel(rules[index]) }}
            </p>
          </div>
          <div class="flex justify-center items-center flex-shrink-0 px-2
                      print:border-solid print:border-t print:border-gray-100"
               :key="`desktop-arrow-${index}`">
            <ArrowDashed class="print:text-orange-400"
                         :class="[
                            {'text-yellow-500': !isResolved},
                            {'text-gray-200': isResolved},
                          ]"/>
          </div>
        </template>
        <div class="flex justify-center items-center relative p-4
                    print:border-solid print:border-t print:border-gray-100 print:p-2
                    print:text-gray-700 print:justify-start"
             :class="[
               {'scanlines--yellow bg-yellow-900': !isResolved},
               {'scanlines--gray': isResolved},
               {'w-1/5': rules.length === 4},
               {'w-1/4': rules.length === 3},
               {'w-1/3': rules.length === 2},
               {'w-1/2': rules.length === 1},
             ]">
          <div class="outcome-corners print:hidden"
                :class="[
                  {'outcome-corners--yellow': !isResolved},
                  {'outcome-corners--gray': isResolved},
                ]"></div>
          <div class="outcome-border-lr print:hidden"
               :class="[
                  {'outcome-border-lr--yellow': !isResolved},
                  {'outcome-border-lr--gray': isResolved},
                ]"></div>
          <div class="outcome-border-tb print:hidden"
               :class="[
                  {'outcome-border-tb--yellow': !isResolved},
                  {'outcome-border-tb--gray': isResolved},
                ]"></div>
          <p class="text-center print:text-left print:text-black print:font-semibold">
            {{ outcome }}
          </p>
        </div>
      </div>
    </div>
    <!-- End Desktop View -->
    <!-- Wrapped View: every trigger remains visible and converges on one outcome. -->
    <div v-if="rules.length > 4"
         class="flex flex-col p-4 print:py-2"
         data-test="wrapped-pathway">
      <div class="wrapped-pathway-flow">
        <div class="wrapped-findings print:text-gray-700">
          <h3 class="uppercase text-xs font-semibold text-gray-200 tracking-widest mb-2
                     print:text-gray-300">
            Findings [{{ rules.length }}]
          </h3>
          <div class="wrapped-rule-grid">
            <div v-for="(rule, ruleIndex) in rules"
                 :key="`wrapped-rule-${rule.id}`"
                 class="wrapped-rule-card">
              <p class="uppercase text-2xs font-semibold text-gray-300 tracking-widest mb-1">
                Finding {{ ruleIndex + 1 }}
              </p>
              <p>{{ ruleLabel(rule) }}</p>
            </div>
          </div>
        </div>
        <div class="wrapped-convergence print:hidden">
          <svg viewBox="0 0 64 100" preserveAspectRatio="none" aria-hidden="true">
            <path v-for="rowIndex in wrappedRowCount"
                  :key="`wrapped-convergence-${rowIndex}`"
                  :d="wrappedConvergencePath(rowIndex)"
                  :class="[
                    {'wrapped-convergence--yellow': !isResolved},
                    {'wrapped-convergence--gray': isResolved},
                  ]" />
          </svg>
        </div>
        <div class="wrapped-outcome flex justify-center items-center relative p-4
                    print:border-solid print:border-t print:border-gray-100 print:text-gray-700"
             :class="[
               {'scanlines--yellow bg-yellow-900': !isResolved},
               {'scanlines--gray': isResolved},
             ]">
          <div class="outcome-corners print:hidden"
               :class="[
                 {'outcome-corners--yellow': !isResolved},
                 {'outcome-corners--gray': isResolved},
               ]"></div>
          <div class="outcome-border-lr print:hidden"
               :class="[
                 {'outcome-border-lr--yellow': !isResolved},
                 {'outcome-border-lr--gray': isResolved},
               ]"></div>
          <div class="outcome-border-tb print:hidden"
               :class="[
                 {'outcome-border-tb--yellow': !isResolved},
                 {'outcome-border-tb--gray': isResolved},
               ]"></div>
          <div>
            <h3 class="uppercase text-xs font-semibold tracking-widest mb-2 text-yellow-500
                       print:text-orange-400">
              Outcome
            </h3>
            <p class="text-center print:text-left print:text-black print:font-semibold">
              {{ outcome }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- End Wrapped View -->
    <!-- Mobile View -->
    <div v-if="rules.length <= 4"
         class="flex flex-col p-4 print:hidden lg:hidden"
         data-test="mobile-pathway">
      <template v-for="(_, ruleIndex) in rules">
        <div class="w-full flex flex-col"
             :key="`mobile-rule-${ruleIndex}`">
          <h3 class="uppercase text-xs font-semibold text-gray-200 tracking-widest ml-2 mb-2">
            Finding {{ ruleIndex + 1 }}
          </h3>
          <div class="p-4 border border-gray-600">
            <p class="text-center">
              {{ ruleLabel(rules[ruleIndex]) }}
            </p>
          </div>
        </div>
        <div class="flex justify-center items-center flex-shrink-0 px-2 mt-6 mb-2"
             :key="`mobile-arrow-${ruleIndex}`">
          <ArrowDashed class="transform rotate-90"
                       :class="[
                            {'text-yellow-500': !isResolved},
                            {'text-gray-200': isResolved},
                          ]"/>
        </div>
      </template>
      <div class="w-full flex flex-col">
        <h3 class="uppercase text-xs font-semibold tracking-widest ml-2 mb-2"
            :class="[
              {'text-gray-200': isResolved},
              {'text-yellow-500': !isResolved},
            ]">
          Outcome
        </h3>
        <div class="relative p-4"
             :class="[
               {'scanlines--yellow bg-yellow-900': !isResolved},
               {'scanlines--gray': isResolved},
             ]">
          <div class="outcome-corners"
               :class="[
                  {'outcome-corners--yellow': !isResolved},
                  {'outcome-corners--gray': isResolved},
                ]"></div>
          <div class="outcome-border-lr"
               :class="[
                  {'outcome-border-lr--yellow': !isResolved},
                  {'outcome-border-lr--gray': isResolved},
                ]"></div>
          <div class="outcome-border-tb"
               :class="[
                  {'outcome-border-tb--yellow': !isResolved},
                  {'outcome-border-tb--gray': isResolved},
                ]"></div>
          <p class="text-center">
            {{ outcome }}
          </p>
        </div>
      </div>
    </div>
    <!-- End Mobile View -->
  </div>
</template>

<script>
import ArrowDashed from '@/assets/arrow_dashed.svg?inline';
import DashedLine from '@/assets/line_dashed.svg?inline';

export default {
  name: 'AttackPathway',
  components: {
    ArrowDashed,
    DashedLine,
  },
  props: {
    /*
     Determines whether to change the styling of the component to the "resolved" styling.
     Shows "resolved" emblem, changes arrows to gray, changes outcome text and box to gray
     */
    isResolved: {
      type: Boolean,
      default: false,
    },
    /*
     The sentence that will be displayed in the "outcome" box
     */
    outcome: {
      type: String,
      required: true,
    },
    /*
     An array of finding rules that will be displayed in the boxes before the outcome box
     */
    rules: {
      type: Array,
      required: true,
    },
    /*
     An array of the total findings for the report, which will be parsed to find the domains
     */
    findings: {
      type: Array,
      required: true,
    },
    /*
     An array of the newly added AttackPathways, to determine if the "New" emblem should be shown
     */
    newAttackPathways: {
      type: Array,
      default: () => ([]),
    },
    /*
      The domain of the report
      (If the findings domain array is not received from the backend, then the parent input
      is the only domain.)
     */
    reportDomain: {
      type: String,
      default: '',
    },
    /*
      A list of domains, so report ids can be retrieved.
     */
    domains: {
      type: Array,
      required: true,
    },
    /*
      The ID of the executive report, used for linking to Domain Report in Found On section.
     */
    executiveReportID: {
      type: String,
      required: true,
    },
    /*
      Domain ID used for "Found On" link. Used for Attack Pathways on the Domain Reports.
     */
    domainId: {
      type: String,
    },
  },
  methods: {
    ruleLabel(rule) {
      return rule.outcome || rule.technologyDescription || 'Matched rule';
    },
    wrappedConvergencePath(rowIndex) {
      const rowY = ((rowIndex - 0.5) / this.wrappedRowCount) * 100;
      return `M2 ${rowY} L42 50 L62 50`;
    },
  },
  computed: {
    wrappedRowCount() {
      return Math.ceil(this.rules.length / 5);
    },
    isNew() {
      return this.newAttackPathways.some((addedPathway) => {
        const pathwayRule = addedPathway && addedPathway.attackPathwayRule;
        if (!pathwayRule || !Array.isArray(pathwayRule.rules)) return false;

        return pathwayRule.outcome === this.outcome
          && pathwayRule.rules.length === this.rules.length
          && pathwayRule.rules
            .every((rule, index) => rule && rule.id === this.rules[index].id);
      });
    },
  },
};
</script>

<style scoped>
.scanlines--yellow {
  background:
    radial-gradient(#251F1C 60%, transparent 90%),
    repeating-linear-gradient(
      0deg,
      #251F1C,
      #251F1C 5px,
      rgba(249, 194, 46, 0.1) 5px,
      rgba(249, 194, 46, 0.1) 6px);
  background-position: center;
  background-size: 100% 110%;
}

.scanlines--gray {
  background:
    radial-gradient(#251F1C 60%, transparent 90%),
    repeating-linear-gradient(
      0deg,
      #251F1C,
      #251F1C 5px,
      rgba(181, 177, 174, 0.1) 5px,
      rgba(181, 177, 174, 0.1) 6px);
  background-position: center;
  background-size: 100% 110%;
}

.wrapped-pathway-flow {
  display: flex;
  align-items: stretch;
}

.wrapped-findings {
  flex: 1 1 auto;
  min-width: 0;
}

.wrapped-rule-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.75rem;
}

.wrapped-rule-card {
  @apply border border-gray-600 p-3;
}

.wrapped-rule-card {
  min-height: 5rem;
}

.wrapped-convergence {
  flex: 0 0 4rem;
  min-height: 6rem;
  padding: 0.75rem 0;
}

.wrapped-convergence svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}

.wrapped-convergence path {
  fill: none;
  stroke-width: 1.5;
  stroke-dasharray: 3 3;
  vector-effect: non-scaling-stroke;
}

.wrapped-convergence--yellow {
  stroke: #f9c22e;
}

.wrapped-convergence--gray {
  stroke: #b5b1ae;
}

.wrapped-outcome {
  flex: 0 0 28%;
  align-self: center;
  min-height: 6rem;
}

@media (max-width: 767px) {
  .wrapped-pathway-flow {
    flex-direction: column;
  }

  .wrapped-rule-grid {
    grid-template-columns: 1fr;
  }

  .wrapped-convergence {
    flex-basis: 3rem;
    min-height: 3rem;
    transform: rotate(90deg);
  }

  .wrapped-outcome {
    flex-basis: auto;
  }
}

@screen print {
  .scanlines--yellow, .scanlines--gray {
    background: white;
  }
}

.outcome-corners {
  @apply absolute;
  top: -4px;
  left: -4px;
  width: calc(100% + 8px);
  height: calc(100% + 8px);
}

.outcome-corners--yellow {
  background:
    linear-gradient(to right, rgba(249, 194, 46, 1) 3px, transparent 3px) 0 0,
    linear-gradient(to right, rgba(249, 194, 46, 1) 3px, transparent 3px) 0 100%,
    linear-gradient(to left, rgba(249, 194, 46, 1) 3px, transparent 3px) 100% 0,
    linear-gradient(to left, rgba(249, 194, 46, 1) 3px, transparent 3px) 100% 100%,
    linear-gradient(to bottom, rgba(249, 194, 46, 1) 3px, transparent 3px) 0 0,
    linear-gradient(to bottom, rgba(249, 194, 46, 1) 3px, transparent 3px) 100% 0,
    linear-gradient(to top, rgba(249, 194, 46, 1) 3px, transparent 3px) 0 100%,
    linear-gradient(to top, rgba(249, 194, 46, 1) 3px, transparent 3px) 100% 100%;
  background-repeat: no-repeat;
  background-size: 15px 15px;
}

.outcome-corners--gray {
  background:
    linear-gradient(to right, rgba(181, 177, 174, 1) 3px, transparent 3px) 0 0,
    linear-gradient(to right, rgba(181, 177, 174, 1) 3px, transparent 3px) 0 100%,
    linear-gradient(to left, rgba(181, 177, 174, 1) 3px, transparent 3px) 100% 0,
    linear-gradient(to left, rgba(181, 177, 174, 1) 3px, transparent 3px) 100% 100%,
    linear-gradient(to bottom, rgba(181, 177, 174, 1) 3px, transparent 3px) 0 0,
    linear-gradient(to bottom, rgba(181, 177, 174, 1) 3px, transparent 3px) 100% 0,
    linear-gradient(to top, rgba(181, 177, 174, 1) 3px, transparent 3px) 0 100%,
    linear-gradient(to top, rgba(181, 177, 174, 1) 3px, transparent 3px) 100% 100%;
  background-repeat: no-repeat;
  background-size: 15px 15px;
}

.outcome-border-tb {
  @apply absolute;
  top: -2px;
  left: -2px;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
}

.outcome-border-tb--yellow {
  background:
    linear-gradient(to right, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 0 0,
    linear-gradient(to left, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 100% 0,
    linear-gradient(to right, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 0 100%,
    linear-gradient(to left, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 100% 100%;
  background-repeat: no-repeat;
  background-size: 50% 2px;
}

.outcome-border-tb--gray {
  background:
    linear-gradient(to right, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 0 0,
    linear-gradient(to left, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 100% 0,
    linear-gradient(to right, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 0 100%,
    linear-gradient(to left, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 100% 100%;
  background-repeat: no-repeat;
  background-size: 50% 2px;
}

.outcome-border-lr {
  @apply absolute;
  top: -2px;
  left: -2px;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
}

.outcome-border-lr--yellow {
  background:
    linear-gradient(to bottom, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 0 0,
    linear-gradient(to bottom, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 100% 0,
    linear-gradient(to top, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 0 100%,
    linear-gradient(to top, rgba(249, 194, 46, 1) 0%, rgba(249, 194, 46, 0.05) 90%) 100% 100%;
  background-repeat: no-repeat;
  background-size: 2px 50%;
}

.outcome-border-lr--gray {
  background:
    linear-gradient(to bottom, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 0 0,
    linear-gradient(to bottom, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 100% 0,
    linear-gradient(to top, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 0 100%,
    linear-gradient(to top, rgba(181, 177, 174, 1) 0%, rgba(181, 177, 174, 0.05) 90%) 100% 100%;
  background-repeat: no-repeat;
  background-size: 2px 50%;
}
</style>
